import Feather from '@expo/vector-icons/Feather';
import { useNavigation } from '@react-navigation/native';
import { Text, Spin, TopMenu } from '_/components';
import Button from '_/components/Button';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import { fontFamily } from '_/config/theme';
import themes from '_/constants/themes';
import { AppRoute, MeetingsProps } from '_/routes/types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, Platform, View, StyleSheet } from 'react-native';
import { TabView, TabBar, SceneMap } from 'react-native-tab-view';
import { useDispatch, useSelector } from 'react-redux';

import { newMeetingRequest } from '_/store/modules/meeting/actions';

import ActiveMeetings from './ActiveMeetings';
import PastMeetings from './PastMeetings';
import { UserCompanyRole } from '../../models/enums';
import { useQuery } from '@tanstack/react-query';
import { useMeetingsContext } from '_/hooks/MeetingsProvider';

const { width } = Dimensions.get('window');

export default function Meetings() {
  const navigation = useNavigation<MeetingsProps>();
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  const { loading } = useSelector((state: any) => state.loading);
  const { role, disableMeetingCreation } = useSelector((state: any) => state.host);
  const companyId = useSelector((state: any) => state.company.id);
  const userId = useSelector((state: any) => state.user.id);

  const [index, setIndex] = useState(0);
  const { getMeetings } = useMeetingsContext();

  const fetchMeetingsForQuery = async () => {
    const data = await getMeetings({ '$sort[start]': -1 });
    return data;
  };

  useQuery({
    queryKey: ['getMeetings', companyId, userId],
    queryFn: fetchMeetingsForQuery,
    staleTime: 1000 * 60 * 5,
  });

  const renderTabBar = (props: any) => (
    <TabBar
      {...props}
      indicatorStyle={{
        backgroundColor: themes.colors.primary,
        height: 3,
        borderRadius: 3,
      }}
      style={{
        backgroundColor: themes.colors.lightQuaternary,
        elevation: 0,
        shadowOpacity: 0,
      }}
      labelStyle={{
        fontFamily: fontFamily.semibold,
        textTransform: 'none',
        fontSize: 14,
      }}
      activeColor={themes.colors.primary}
      inactiveColor={themes.colors.graySecondary}
    />
  );

  const renderScene = SceneMap({
    first: ActiveMeetings,
    second: PastMeetings,
  });

  const initialLayout = { width: Dimensions.get('window').width };

  function handleCreateMeeting() {
    dispatch(newMeetingRequest());
    navigation.navigate(AppRoute.EDIT_MEETING);
  }

  return (
    <ContainerWidthLimit>
      <View style={styles.container}>
        <Spin visible={loading} />
        <TopMenu />
        <TabView
          lazy
          onIndexChange={setIndex}
          renderScene={renderScene}
          renderTabBar={renderTabBar}
          initialLayout={initialLayout}
          navigationState={{
            index,
            routes: [
              { key: 'first', title: translate('meetings.activeMeetingsTitle') },
              { key: 'second', title: translate('meetings.pastMeetingsTitle') },
            ],
          }}
        />

        {[
          UserCompanyRole.ADMIN,
          UserCompanyRole.MANAGER,
          UserCompanyRole.FRONTDESK,
          UserCompanyRole.HOST,
        ]?.includes(role) &&
          !disableMeetingCreation && (
            <Button onPress={handleCreateMeeting} buttonStyles={styles.btnCreateMeeting}>
              <Feather name="plus" style={styles.icon} />
              <Text style={styles.text}>{translate('meetings.newMeeting')}</Text>
            </Button>
          )}
      </View>
    </ContainerWidthLimit>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: themes.colors.lightQuaternary,
    flex: 1,
  },
  btnCreateMeeting: {
    alignSelf: 'center',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    bottom: 24,
    height: 44,
    borderRadius: 6,
    ...(Platform.OS === 'web' ? {} : { width: width - 48 }),
  },
  icon: {
    fontSize: 14,
    color: themes.colors.white,
  },
  text: {
    fontSize: 18,
    color: themes.colors.white,
    marginLeft: 8,
  },
});
